<template>
	<article class="question-display">
		<div class="sr-only">
			<p>
				{{ $t('MAIN_CONTENT.testTaker.questionNumber', { number: activeQuestion.index + 1 } ) }}
			</p>
		</div>

		<div class="pull-right">
			<span class="pill pill-dark">
				{{ $t('MAIN_CONTENT.testTaker.pointsPossible', { points: activeQuestion.TOTAL_POINTS } ) }}
			</span>
		</div>

		<div class="question-type-instructions">
			<strong>{{ activeQuestion.QUESTION_NAME }}</strong>
			<span class="hint">
				{{ $t('MAIN_CONTENT.EssayQuestion.enterYourAnswer') }}
			</span>
		</div>

		<h3 class="question-text" v-html="activeQuestion.QUESTION_TEXT" />

		<div class="answers">
			<div :class="[ 'answer', getAnswerClass() ]">
				<div :class="[ 'answer-number',{ selected:answeredByStudent() } ]">
					<i v-if="answeredByStudent()" class="fa fas fa-arrow-circle-right" />
					<!-- not showing points here -->
				</div>
				<div class="answer-text form">
					<textarea 
							v-if="!this.isGraded()"
							ref="answerRef" 
							v-model="answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT"
							class="form-control" 
							style="min-width: 400px; height: 200px"
							@blur="answerTheQuestion()" 
							@keyup="schedAnswerTheQuestion()" />
					<div v-else v-html="answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT" />
				</div>

				<div class="grade-result">
					<i :class="getFeedbackIcon()" />
				</div>
			</div>

			<div v-if="!this.isGraded( activeQuestion )">
				<small>
					<span v-if="answerTooShort || answerTooLong">
						{{ $t('MAIN_CONTENT.EssayQuestion.minMaxPointsMessage', { min: activeQuestion.MIN_LENGTH, max: activeQuestion.MAX_LENGTH } ) }}
					</span>
				</small>
			</div>
		</div>

		<question-summary />

		<test-buttons v-if="testMode !== 'grading'" @answer-save="submitAnswer" />
	</article>
</template>

<script>
	import { mapState,mapActions,mapMutations } from "vuex";
	import testButtons from "./testButtons.vue";
	import questionSummary from "./questionSummary.vue";

	export default {
		components : { testButtons, questionSummary },
		props      : {
			testPreview: {
				type     : Object,
				required : true
			},
			activeQuestion: {
				type     : Object,
				required : true
			},
			studentAnswer: {
				type    : Array,
				default : function(){
					return {};
				}
			},
			studentAnswers: {
				type    : Object,
				default : function(){
					return {};
				}
			},
			blankAnswer: {
				type    : Object,
				default : function(){
					return {};
				}
			},
			testMode: {
				type    : String,
				default : "student"
			}
		},
		data() {
				return {
						answerData: {
							testId     : "",
							questionId : "",
							answer     : []
						},
						timer: null
				};
		},
		computed: {
				allowChanges (){
						return this.testPreview.TEST.ALLOW_ANSWER_CHANGE;
				},
				answerTooShort (){
						return  this.activeQuestion.MIN_LENGTH ? this.activeQuestion.MIN_LENGTH > this.answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT.length : false;
				},
				answerTooLong (){
						return  this.activeQuestion.MAX_LENGTH ? this.activeQuestion.MAX_LENGTH < this.answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT.length : false;
				}
		},
		watch: {
				activeQuestion: {
						immediate: true,
						handler: function(newValue, oldValue) {
								this.answerData.questionId = this.activeQuestion.TEST_QUESTION_RID;
								this.answerData.testId = this.activeQuestion.TEST_RID;
								this.populateForm();
								this.$nextTick(()=>{
										try{
												document.getElementById( "testZone" ).focus();
										}catch(e){}
								})
						},
				}
		},
		methods: {
				...mapMutations( [ "updateState","setUnsaved" ] ),
				...mapActions( [ "update","scoreAnswer","postAnswer" ] ),
				schedAnswerTheQuestion() {
						/* post answer when user stops typing for 2 second */
						if (this.timer) {
								clearTimeout(this.timer);
								this.timer = null;
						}
						this.timer = setTimeout(() => {
								this.answerTheQuestion();
						}, 2000);
						
				},

				isSelected ( answer ){
						return this.studentAnswer.map( ans =>{
								return ans.ANSWER_RID; 
						} ).indexOf( answer.ANSWER_RID ) > -1;
				},
				// returns true if question has been graded
				isGraded (){
						return this.testMode === "grading" || (this.studentAnswer && this.studentAnswer.filter( ans =>{
								return ans.QUESTION_GRADED_STATUS; 
						} ).length > 0);
				},
				// will return css class for check if correct and x for incorrect for graded answers
				getFeedbackIcon ( answer ){
						if( !this.isGraded( answer ) ) return "";
						let itemCorrect = this.isCorrect( answer );
						return itemCorrect === true ? "fa fas fa-check-circle" :"fa fas fa-times-circl";
				},
				// returns css class if graded correct
				getAnswerClass ( answer ){
						if( !this.isGraded() ) return "";
						if(this.studentAnswer.filter( ans =>{
								return ans.QUESTION_GRADED_STATUS === "P"; 
						} ).length > 0){
								return "";
						}
						return this.isGraded() && this.isCorrect( answer ) ? "correct" : "incorrect";
				},
				// compares answer against answer key
				isCorrect (){
						if( ! this.isGraded() ) return false;
						return this.studentAnswer[ 0 ].POINTS_EARNED > 0;
				},
				// did the student submit an answer 
				answeredByStudent (){
						return  Object.prototype.hasOwnProperty.call( this.studentAnswers, this.activeQuestion.TEST_QUESTION_RID );
				},
				prepareAnswer (){
						// prepare payload with activeQuestion id to use as a key
						this.answerData.questionId = this.activeQuestion.TEST_QUESTION_RID;
						this.answerData.testId = this.activeQuestion.TEST_RID;
						this.answerData.questionType = this.activeQuestion.QUESTION_TYPE;
						// populate it
						this.answerData.answer[ 0 ].TEST_RID = this.activeQuestion.TEST_RID;
						this.answerData.answer[ 0 ].TEST_QUESTION_RID = this.activeQuestion.TEST_QUESTION_RID;
				},
				answerTheQuestion (){
						// if blank, do not mark as answered and do not grade if immediate feedback is on
						if( !this.answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT.length  ) return;
						// question has been answered, so no changes
						if( this.isGraded() && !this.allowChanges ) return;
						this.prepareAnswer();
						
						// clear this answer from unsaved
						this.setUnsaved( { key: this.answerData.questionId, value: JSON.parse( JSON.stringify(this.answerData)) } );
						// save the answer


						this.postAnswer( { 
										"key": this.answerData.questionId,
										"data": JSON.stringify(this.answerData),
										"queryParams":{},
										"routeParams":{ 
												action: "answer-question", 
												testRid: this.$route.params.testRid
										} 
								}
						).then(()=>{
								// clear this answer from unsaved
								this.setUnsaved( { key: this.answerData.questionId, value: "" } );
						});
				},
				populateForm (){
						// start with the blank answer obj
						let blankAns = [];
						blankAns.push(JSON.parse( JSON.stringify( this.blankAnswer ) ));
						this.$set(this.answerData, "answer", blankAns);

						if( this.studentAnswer ){
								this.$set(this.answerData, "answer", this.studentAnswer);
						}
				},
				focusInput ( inputRef ) {
						this.$refs[inputRef].focus();
				},
				pointsEarned (){
						let total = 0;
						this.studentAnswer.forEach( ans =>{
								total += ans.POINTS_EARNED;
						} );
						return total;
				},
				submitAnswer (){
						if( !this.answerData.questionId ) return;
						// tracking unsaved responses
						this.setUnsaved( { key: this.answerData.questionId, value: "" } );
						this.scoreAnswer( { 
										"key": this.answerData.questionId,
										"data": JSON.stringify(this.answerData),
										"queryParams":{},
										"routeParams":{ 
												action: "score-question", 
												testRid: this.$route.params.testRid
										} 
								}
						);
				}
		},
		created (){
				this.populateForm();
		},
		mounted (){
				try{
						document.getElementById( "testZone" ).focus();
				}catch(e){}
		}
	};
</script>
